import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import createCreditItemColumns from "../Utils/CreditItemColumns";
import CreditFormLayout from "./CreditFormLayout";
import {
  getAllPartners,
  getAllProducts,
  getAllCategories,
  getAccountTree,
  getCreditById,
  createCreditWithFile,
  updateCreditWithFile,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import { getOffersByBrand } from "../../../../../core/api/marketing";
import dayjs from "dayjs";
import {
  calculateTotals,
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  prepareFormData,
} from "../../../utils/invoiceUtils";

const CreateCredit = ({
  selectedBrand,
  creditId,
  onSuccess,
  defaultCreditType = "bill",
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const [creditItems, setCreditItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(selectedBrand);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_taxAmount: 0,
    calculated_discountAmount: 0,
    calculated_total: 0,
    match: true,
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // Data fetching functions
  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetchBrandsBySelectedClients(selectedClients);
      const brandsArray = response.data || [];
      setBrands(brandsArray);
      setFilteredBrands(brandsArray);
    } catch (error) {
      console.error("Error fetching brands:", error);
      message.error("Failed to fetch brands");
    }
  }, [selectedClients]);

  const fetchPartners = useCallback(async () => {
    try {
      if (!currentBrand) return; // Don't fetch if no brand selected
      const filters = { brand: currentBrand.id };
      const response = await getAllPartners(filters);
      // Get creditType from form, fallback to defaultCreditType prop
      const creditType = form.getFieldValue("creditType") || defaultCreditType;
      // Filter partners based on credit type
      let filteredPartners = response.data;
      if (creditType === "bill") {
        // For bill credits, only show vendors
        filteredPartners = response.data.filter(
          (partner) =>
            partner.partner_type === "vendor" || partner.partner_type === "both"
        );
      } else if (creditType === "invoice") {
        // For invoice credits, only show customers
        filteredPartners = response.data.filter(
          (partner) =>
            partner.partner_type === "customer" ||
            partner.partner_type === "both"
        );
      }
      console.log(
        `Filtered partners for ${creditType}:`,
        filteredPartners.length
      );
      setPartners(filteredPartners);
    } catch (error) {
      console.error("Error fetching partners:", error.response || error);
      message.error("Failed to fetch partners");
    }
  }, [currentBrand, defaultCreditType, form]);

  const fetchProducts = useCallback(
    async (vendorId = null) => {
      try {
        // Add brand and vendor filters if selected
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllProducts(filters);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error.response || error);
        message.error("Failed to fetch products");
      }
    },
    [currentBrand, setProducts]
  );

  const fetchCategories = useCallback(
    async (vendorId = null) => {
      try {
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllCategories(filters);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error.response || error);
        message.error("Failed to fetch categories");
      }
    },
    [currentBrand, setCategories]
  );

  const fetchAccounts = useCallback(async () => {
    try {
      // Add brand filter if a brand is selected
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      // Use account tree API to get hierarchical structure
      const response = await getAccountTree(filters);
      setAccounts(response);
    } catch (error) {
      console.error("Error fetching account tree:", error.response || error);
      message.error("Failed to fetch chart of accounts");
    }
  }, [currentBrand, setAccounts]);

  const fetchOffers = useCallback(async () => {
    try {
      if (!currentBrand) {
        setOffers([]);
        return;
      }
      const res = await getOffersByBrand(currentBrand.id);
      // Some APIs return {data: []}, others return [] directly; normalize
      const list = Array.isArray(res?.data) ? res.data : res;
      setOffers(list || []);
    } catch (error) {
      console.error("Error fetching offers:", error.response || error);
      message.error("Failed to fetch offers");
    }
  }, [currentBrand]);

  const handleBrandChange = useCallback(
    (brandId) => {
      const brand = brands.find((b) => b.id === brandId);
      if (brand) {
        setCurrentBrand(brand);
        // Reset partner and items when brand changes
        form.setFieldsValue({ partner: undefined });
        setCreditItems([]);
        // Clear dependent data when brand changes
        setPartners([]);
        setProducts([]);
        setCategories([]);
        setAccounts([]);
        setOffers([]);
        // Fetch partners and accounts immediately when brand changes
        if (brand) {
          const filters = { brand: brand.id };
          // Get creditType from form, fallback to defaultCreditType prop
          const creditType = form.getFieldValue("creditType") || defaultCreditType;
          // Fetch partners
          getAllPartners(filters)
            .then((response) => {
              // Filter partners based on credit type
              let filteredPartners = response.data;
              if (creditType === "bill") {
                // For bill credits, only show vendors
                filteredPartners = response.data.filter(
                  (partner) =>
                    partner.partner_type === "vendor" ||
                    partner.partner_type === "both"
                );
              } else if (creditType === "invoice") {
                // For invoice credits, only show customers
                filteredPartners = response.data.filter(
                  (partner) =>
                    partner.partner_type === "customer" ||
                    partner.partner_type === "both"
                );
              }
              console.log(
                `Filtered partners for ${creditType} (brand change):`,
                filteredPartners.length
              );
              setPartners(filteredPartners);
            })
            .catch((error) => {
              console.error("Error fetching partners:", error.response || error);
              message.error("Failed to fetch partners");
            });
          
          // Fetch accounts immediately when brand changes
          fetchAccounts();
        }
      }
    },
    [brands, form, defaultCreditType, fetchAccounts]
  );

  const handlePartnerChange = (value) => {
    const selectedPartner = partners.find((p) => p.id === value);
    if (selectedPartner) {
      // Filter brands based on partner
      if (selectedPartner.brands && selectedPartner.brands.length > 0) {
        const partnerBrandIds = selectedPartner.brands;
        const filtered = brands.filter((b) => partnerBrandIds.includes(b.id));
        setFilteredBrands(filtered);
      } else if (selectedPartner.brand) {
        // Legacy single brand
        const filtered = brands.filter((b) => b.id === selectedPartner.brand);
        setFilteredBrands(filtered);
      } else {
        setFilteredBrands(brands);
      }

      // If the selected partner is a vendor, fetch products and categories for this vendor
      if (
        selectedPartner.partner_type === "vendor" ||
        selectedPartner.partner_type === "both"
      ) {
        fetchProducts(selectedPartner.id);
        fetchCategories(selectedPartner.id);
      } else {
        // If not a vendor, fetch all products and categories for the selected brand
        fetchProducts();
        fetchCategories();
      }
    }
  };

  // Fetch credit data for editing
  const fetchCreditData = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const creditData = await getCreditById(id);
        console.log("Fetched credit data:", creditData);

        // Format the data for the form
        const formattedItems = creditData.items.map((item) => {
          // Derive offer details and first product variation if available
          const offerDetails = item.offer_details || {};
          const variations = Array.isArray(
            offerDetails.product_variations_detail
          )
            ? offerDetails.product_variations_detail
            : [];
          const firstVariation = variations.length > 0 ? variations[0] : null;

          // Prefer explicit fields; fallback to offer details/variation meta
          const derivedProductName =
            item.product_name ||
            firstVariation?.name ||
            offerDetails.name ||
            "";

          const derivedProductVariation =
            item.product_variation ||
            firstVariation?.description ||
            offerDetails.product_variation ||
            offerDetails.variation ||
            item.description ||
            "";

          // Prefer item.product; fallback to variation's product_id if present
          const derivedProductId =
            item.product || firstVariation?.product_id || null;

          const normalizedOfferId =
            item.offer || item.offer_id || offerDetails?.id || null;

          return {
            key: item.id,
            productId: derivedProductId,
            categoryId: item.category,
            accountId: item.account || item.account_id || null,
            productName: derivedProductName,
            productVariation: derivedProductVariation,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            taxRate: item.tax_rate,
            taxAmount: item.tax_amount,
            discountAmount: item.discount_amount || 0,
            subtotal: item.subtotal,
            total_amount: item.total,
            // Normalize offer
            offerId: normalizedOfferId,
            offerIds: normalizedOfferId ? [normalizedOfferId] : [],
          };
        });

        // Extract brand and partner IDs (they might be objects or IDs)
        const brandId = typeof creditData.brand === 'object' ? creditData.brand?.id : creditData.brand;
        const partnerId = typeof creditData.partner === 'object' ? creditData.partner?.id : creditData.partner;
        const creditType = creditData.credit_type || defaultCreditType;
        
        // Update currentBrand state if the credit has a different brand
        let targetBrand = currentBrand;
        if (brandId) {
          const creditBrand = brands.find(b => b.id === brandId);
          if (creditBrand) {
            targetBrand = creditBrand;
            setCurrentBrand(creditBrand);
            setFilteredBrands([creditBrand]);
          }
        }

        // Fetch partners for the brand BEFORE setting form values
        // This ensures the partner dropdown has options when the value is set
        if (targetBrand) {
          try {
            const filters = { brand: targetBrand.id };
            const partnersResponse = await getAllPartners(filters);
            // Filter partners based on credit type
            let filteredPartners = partnersResponse.data || [];
            if (creditType === "bill") {
              filteredPartners = filteredPartners.filter(
                (partner) =>
                  partner.partner_type === "vendor" || partner.partner_type === "both"
              );
            } else if (creditType === "invoice") {
              filteredPartners = filteredPartners.filter(
                (partner) =>
                  partner.partner_type === "customer" || partner.partner_type === "both"
              );
            }
            setPartners(filteredPartners);
          } catch (error) {
            console.error("Error fetching partners for credit edit:", error);
          }
        }

        // Set form values AFTER partners are loaded
        form.setFieldsValue({
          partner: partnerId,
          brand: brandId,
          issue_date: creditData.issue_date ? dayjs(creditData.issue_date) : dayjs(),
          creditType: creditType,
          number: creditData.number,
          currency: creditData.currency,
          notes: creditData.notes,
          subtotal: creditData.subtotal || 0,
          discount_amount: creditData.discount_amount || 0,
          tax_rate: creditData.tax_rate || 0,
          total_amount: creditData.total_amount || 0,
          // Handle file URL if present
          fileUrl: creditData.file || null,
        });

        setCreditItems(formattedItems);

        // Calculate values using the same utility as when creating credit
        // Use setTimeout to ensure state is updated before calculating
        setTimeout(() => {
          calculateCreditTotals(formattedItems);
        }, 100);
      } catch (error) {
        console.error("Error fetching credit data:", error);
        message.error("Failed to fetch credit data");
      } finally {
        setLoading(false);
      }
    },
    [form, setCreditItems, setCalculatedValues, setLoading, brands, currentBrand, setCurrentBrand, setFilteredBrands, defaultCreditType, setPartners]
  );

  // Credit items management
  const addItem = () => {
    const updatedItems = [
      ...creditItems,
      {
        key: Date.now(),
        productId: null,
        categoryId: null,
        accountId: null,
        productName: "",
        productVariation: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        taxAmount: 0,
        discountAmount: 0,
        subtotal: 0,
        total: 0,
        offerId: null,
        offerIds: [],
      },
    ];
    setCreditItems(updatedItems);
    // Recalculate totals after adding item
    calculateCreditTotals(updatedItems);
  };

  const removeItem = (key) => {
    const updatedItems = creditItems.filter((item) => item.key !== key);
    setCreditItems(updatedItems);
    calculateCreditTotals(updatedItems);
  };

  const handleProductChange = (value, key) => {
    const updatedItems = handleProductChangeUtil(
      value,
      key,
      products,
      creditItems
    );
    if (updatedItems !== creditItems) {
      setCreditItems(updatedItems);
      // Recalculate totals after product change
      calculateCreditTotals(updatedItems);
    }
  };

  const handleItemChange = (key, field, value) => {
    const updatedItems = handleItemChangeUtil(key, field, value, creditItems);
    setCreditItems(updatedItems);
    // Recalculate totals after item change
    calculateCreditTotals(updatedItems);
  };

  const handleCreditError = (error, isEditMode) => {
    const action = isEditMode ? "update" : "create";

    // 1. Top‐level detail error
    if (error.response?.data?.detail) {
      message.error(
        `Failed to ${action} credit: ${error.response.data.detail}`
      );
      return;
    }

    // 2. Field‐level validation errors
    const errs = error.response?.data;
    if (errs && typeof errs === "object") {
      Object.entries(errs).forEach(([field, messages]) => {
        messages.forEach((msg) => {
          message.error(`${field}: ${msg}`);
        });
      });
      return;
    }

    // 3. Fallback
    message.error(`Failed to ${action} credit: ${error.message}`);
  };

  const calculateCreditTotals = useCallback(
    (items, formInstance) => {
      const calculatedValues = calculateTotals(items, formInstance || form);
      setCalculatedValues(calculatedValues);

      // Update form fields immediately with calculated values
      if (formInstance || form) {
        const formToUpdate = formInstance || form;
        formToUpdate.setFieldsValue({
          subtotal: calculatedValues.calculated_subtotal,
          total_amount: calculatedValues.calculated_total,
        });
      }

      return calculatedValues;
    },
    [form, setCalculatedValues]
  );

  const getCurrentCurrency = () =>
    form ? form.getFieldValue("currency") || "$" : "$";

  const onFinish = async (values) => {
    if (!currentBrand) {
      message.error("Please select a brand before saving the credit");
      return;
    }

    // Check if total amount matches calculated total
    const formTotalAmount = parseFloat(form.getFieldValue("total_amount") || 0);
    const calculatedTotal = calculatedValues.calculated_total;

    // If totals don't match, show confirmation dialog instead of preventing submission
    if (Math.abs(formTotalAmount - calculatedTotal) > 0.01) {
      const { Modal } = await import("antd");

      Modal.confirm({
        title: "Total Amount Mismatch",
        content: `The total amount (${formTotalAmount}) doesn't match the calculated total (${calculatedTotal}). Do you want to proceed anyway?`,
        okText: "Yes, proceed",
        cancelText: "No, I'll fix it",
        onOk: async () => {
          // Continue with submission if user confirms
          await submitCredit(values);
        },
      });
      return;
    }

    // If totals match, proceed with submission
    await submitCredit(values);
  };

  // Helper function to submit the credit
  const submitCredit = async (values) => {
    try {
      setLoading(true);

      // Format the data for submission
      const creditData = {
        ...values,
        // Map camelCase field names to snake_case for backend
        credit_type: values.creditType,
        issue_date: values.issue_date
          ? (dayjs.isDayjs(values.issue_date) 
              ? values.issue_date.format("YYYY-MM-DD")
              : dayjs(values.issue_date).format("YYYY-MM-DD"))
          : dayjs().format("YYYY-MM-DD"),
        brand: currentBrand.id,
        // Use calculated values instead of form values to ensure accuracy, rounded to 3 decimal places
        subtotal: Number(Number(calculatedValues.calculated_subtotal || 0).toFixed(3)),
        discount_amount: Number(
          Number(calculatedValues.calculated_discount_amount || 0).toFixed(3)
        ),
        total_amount: Number(Number(calculatedValues.calculated_total || 0).toFixed(3)),
        items: creditItems.map((item) => ({
          product: item.productId,
          category: item.categoryId,
          account: item.accountId, // Account is required
          offer: item.offerId,
          description: item.description,
          quantity: Number(item.quantity),
          // Round unit_price to 3 decimal places
          unit_price: Number(Number(item.unitPrice || 0).toFixed(3)),
          tax_rate: Number((parseFloat(item.taxRate) || 0).toFixed(3)),
          tax_amount: Number(Number(item.taxAmount || 0).toFixed(3)),
          discount_amount: Number(Number(item.discountAmount || 0).toFixed(3)),
          subtotal: Number(Number(item.subtotal || 0).toFixed(3)),
          total: Number(Number(
            item.total || item.subtotal + item.taxAmount - item.discountAmount
          ).toFixed(3)),
        })),
      };

      // Remove camelCase fields that have been converted to snake_case
      delete creditData.creditType;

      console.log("Submitting credit with data:", creditData);

      // Prepare form data for submission (handles file uploads)
      const formData = prepareFormData(creditData, values);

      if (isEditMode) {
        await updateCreditWithFile(creditId, formData);
        message.success("Credit updated successfully");
      } else {
        await createCreditWithFile(formData);
        message.success("Credit created successfully");
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/accounting/payment-allocation/credits"); // fallback
      }
    } catch (error) {
      handleCreditError(error, isEditMode);
    } finally {
      setLoading(false);
    }
  };

  // Initialize form with default values
  useEffect(() => {
    // Make sure form is defined before setting values
    if (form && !isEditMode) {
      form.setFieldsValue({
        currency: "$",
        creditType: defaultCreditType, // Use the prop value
        brand: currentBrand?.id || undefined,
        issue_date: dayjs(), // Form uses snake_case
        subtotal: 0,
        discount_amount: 0,
        tax_rate: 0,
        total_amount: 0,
      });
    }
  }, [form, defaultCreditType, currentBrand, isEditMode]);

  // Handle form value changes
  const onValuesChange = (changedValues) => {
    // Recalculate totals when form values change (tax_rate, discount_amount, etc.)
    if (
      "discount_amount" in changedValues ||
      "tax_rate" in changedValues ||
      "total_amount" in changedValues
    ) {
      calculateCreditTotals(creditItems);
    }

    // If creditType changed, refetch partners with new filter
    if (changedValues.creditType !== undefined) {
      // Get the new creditType from the form (use changed value, fallback to form value, then default)
      const newCreditType =
        changedValues.creditType ||
        form.getFieldValue("creditType") ||
        defaultCreditType;
      // Refetch partners with the new creditType filter
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      getAllPartners(filters)
        .then((response) => {
          // Filter partners based on the new credit type
          let filteredPartners = response.data;
          if (newCreditType === "bill") {
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "vendor" ||
                partner.partner_type === "both"
            );
          } else if (newCreditType === "invoice") {
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "customer" ||
                partner.partner_type === "both"
            );
          }
          console.log(
            `Filtered partners for ${newCreditType} (onValuesChange):`,
            filteredPartners.length
          );
          setPartners(filteredPartners);
        })
        .catch((error) => {
          console.error("Error fetching partners:", error.response || error);
          message.error("Failed to fetch partners");
        });
    }
  };

  // Initialize currentBrand from selectedBrand prop
  useEffect(() => {
    if (selectedBrand) {
      setCurrentBrand(selectedBrand);
    }
  }, [selectedBrand]);

  // Fetch brands on mount
  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Fetch data when currentBrand changes
  useEffect(() => {
    if (currentBrand) {
      // Ensure creditType is set in form before fetching partners
      const currentCreditType = form.getFieldValue("creditType");
      if (!currentCreditType) {
        form.setFieldsValue({ creditType: defaultCreditType });
      }
      fetchPartners();
      fetchCategories();
      fetchAccounts();
      fetchOffers();
    } else {
      // Clear all data when no brand is selected
      setPartners([]);
      setProducts([]);
      setCategories([]);
      setAccounts([]);
      setOffers([]);
    }
  }, [
    currentBrand,
    fetchPartners,
    fetchCategories,
    fetchAccounts,
    fetchOffers,
    form,
    defaultCreditType,
  ]);

  // Load credit data when editing (creditId is provided) - wait for brands to be loaded
  useEffect(() => {
    if (creditId && brands.length > 0) {
      setIsEditMode(true);
      fetchCreditData(creditId);
    }
  }, [creditId, brands, fetchCreditData]);

  const columns = useMemo(
    () =>
      createCreditItemColumns(
        products,
        categories,
        accounts,
        offers,
        handleProductChange,
        handleItemChange,
        getCurrentCurrency,
        setCreditItems,
        calculateCreditTotals,
        removeItem,
        getCurrentCurrency(),
        navigate
      ),
    [
      products,
      categories,
      accounts,
      offers,
      handleProductChange,
      handleItemChange,
      getCurrentCurrency,
      setCreditItems,
      calculateCreditTotals,
      removeItem,
      navigate,
    ]
  );

  return (
    <CreditFormLayout
      form={form}
      isEditMode={isEditMode}
      loading={loading}
      invoiceItems={creditItems}
      partners={partners}
      categories={categories}
      columns={columns}
      calculatedValues={calculatedValues}
      onFinish={onFinish}
      handlePartnerChange={handlePartnerChange}
      addItem={addItem}
      navigate={navigate}
      selectedBrand={currentBrand}
      brands={filteredBrands}
      allBrands={brands}
      onBrandChange={handleBrandChange}
      onValuesChange={onValuesChange}
      formTitle={isEditMode ? "Edit Credit" : "Create Credit"}
      formType="credit"
      defaultCreditType={defaultCreditType}
    />
  );
};

export default CreateCredit;
